import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BarChart3,
  Package,
  ShoppingCart,
  Users,
  Plus,
  ListOrdered,
  Tag,
  Mail,
  TrendingUp,
  Activity,
  Star,
  Zap,
} from 'lucide-react'
import {
  useDashboardOverview,
  useRecentOrders,
  useNewCustomers,
  useLowStockProducts,
  useTopProducts,
  useProductStats,
  useCustomerStats,
  useReviewStats,
  useCouponStats,
} from '../hooks/useDashboardData'
import toast from 'react-hot-toast'
import StatCard from '../components/StatCard'
import Card from '../components/Card'
import { RecentOrdersTable, RecentUsersTable, LowStockProductsTable, TopProductsTable, KPICards, BusinessInsights } from '../components/dashboard'
import { OrdersChart } from '../components/charts'

interface QuickAction {
  label: string
  route: string
  icon: React.ReactNode
  color: string
}

interface DashboardData {
  overview: {
    totalOrders: number
    totalCustomers: number
    totalProducts: number
    totalRevenue: number
  }
  productStats: {
    totalProducts: number
    activeProducts: number
    lowStockProducts: number
    outOfStockProducts: number
  }
  customerStats: {
    totalCustomers: number
    activeCustomers: number
    newCustomersThisMonth: number
  }
  reviewStats: {
    totalReviews: number
    approvedReviews: number
    pendingReviews: number
    rejectedReviews: number
    averageRating: number
  }
  couponStats: {
    totalCoupons: number
    activeCoupons: number
    totalUsed: number
  }
  orderStats: {
    totalOrders: number
    totalRevenue: number
    byStatus: {
      PENDING: number
      CONFIRMED: number
      PROCESSING: number
      SHIPPED: number
      DELIVERED: number
      CANCELLED: number
    }
  }
  recentOrders: any[]
}

export default function DashboardPage() {
  const navigate = useNavigate()

  // Use optimized React Query hooks with built-in caching
  const { data: overview } = useDashboardOverview()
  const { data: recentOrders } = useRecentOrders(10)
  const { data: recentUsers } = useNewCustomers(10)
  const { data: lowStockProducts } = useLowStockProducts(10)
  const { data: topProducts } = useTopProducts(10)
  const { data: productStats } = useProductStats()
  const { data: customerStats } = useCustomerStats()
  const { data: reviewStats } = useReviewStats()
  const { data: couponStats } = useCouponStats()

  const loading = !overview

  const dashboard: DashboardData | null = overview ? {
    overview: overview,
    recentOrders: recentOrders || [],
    orderStats: { totalOrders: 0, totalRevenue: 0, byStatus: { PENDING: 0, CONFIRMED: 0, PROCESSING: 0, SHIPPED: 0, DELIVERED: 0, CANCELLED: 0 } },
    productStats: productStats || { totalProducts: 0, activeProducts: 0, lowStockProducts: 0, outOfStockProducts: 0 },
    customerStats: customerStats || { totalCustomers: 0, activeCustomers: 0, newCustomersThisMonth: 0 },
    reviewStats: reviewStats || { totalReviews: 0, approvedReviews: 0, pendingReviews: 0, rejectedReviews: 0, averageRating: 0 },
    couponStats: couponStats || { totalCoupons: 0, activeCoupons: 0, totalUsed: 0 },
  } : null

  const quickActions: QuickAction[] = [
    {
      label: 'Add Product',
      route: '/products',
      icon: <Plus className="w-5 h-5" />,
      color: 'text-blue-600',
    },
    {
      label: 'Orders',
      route: '/orders',
      icon: <ListOrdered className="w-5 h-5" />,
      color: 'text-green-600',
    },
    {
      label: 'Customers',
      route: '/customers',
      icon: <Users className="w-5 h-5" />,
      color: 'text-amber-600',
    },
    {
      label: 'Payments',
      route: '/payments',
      icon: <BarChart3 className="w-5 h-5" />,
      color: 'text-emerald-600',
    },
    {
      label: 'Reviews',
      route: '/reviews',
      icon: <Star className="w-5 h-5" />,
      color: 'text-purple-600',
    },
    {
      label: 'Coupons',
      route: '/coupons',
      icon: <Tag className="w-5 h-5" />,
      color: 'text-pink-600',
    },
    {
      label: 'Contact Messages',
      route: '/contact-messages',
      icon: <Mail className="w-5 h-5" />,
      color: 'text-orange-600',
    },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#6D3A0D]"></div>
      </div>
    )
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Failed to load dashboard data</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-[#6D3A0D]">Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Welcome back! Here's an overview of your store performance.
        </p>
      </div>

      {/* Overview Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={<ShoppingCart className="text-[#6D3A0D]" size={24} />}
          label="Total Orders"
          value={dashboard.overview.totalOrders}
        />
        <StatCard
          icon={<BarChart3 className="text-emerald-600" size={24} />}
          label="Total Revenue"
          value={`₹${Number(dashboard.overview.totalRevenue || 0).toFixed(2)}`}
        />
        <StatCard
          icon={<Users className="text-amber-600" size={24} />}
          label="Total Customers"
          value={dashboard.overview.totalCustomers}
        />
        <StatCard
          icon={<Package className="text-[#8B4513]" size={24} />}
          label="Total Products"
          value={dashboard.overview.totalProducts}
        />
      </div>

      {/* Product Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={<Package className="text-blue-600" size={24} />}
          label="Active Products"
          value={dashboard.productStats.activeProducts}
        />
        <StatCard
          icon={<Zap className="text-yellow-600" size={24} />}
          label="Low Stock Products"
          value={dashboard.productStats.lowStockProducts}
        />
        <StatCard
          icon={<Package className="text-red-600" size={24} />}
          label="Out of Stock"
          value={dashboard.productStats.outOfStockProducts}
        />
        <StatCard
          icon={<TrendingUp className="text-green-600" size={24} />}
          label="New Customers This Month"
          value={dashboard.customerStats.newCustomersThisMonth}
        />
      </div>

      {/* Reviews & Coupons Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          icon={<Star className="text-yellow-600" size={24} />}
          label="Total Reviews"
          value={dashboard.reviewStats.totalReviews}
        />
        <StatCard
          icon={<Star className="text-green-600" size={24} />}
          label="Approved Reviews"
          value={dashboard.reviewStats.approvedReviews}
        />
        <StatCard
          icon={<Tag className="text-pink-600" size={24} />}
          label="Active Coupons"
          value={dashboard.couponStats.activeCoupons}
        />
      </div>

      {/* Detailed Stats Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Status Breakdown */}
        <Card title="Order Status Breakdown" subtitle="Orders by status">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Delivered</span>
              <span className="text-lg font-bold text-green-600">{dashboard.orderStats.byStatus.DELIVERED}</span>
            </div>
            <div className="h-px bg-gray-200"></div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Shipped</span>
              <span className="text-lg font-bold text-blue-600">{dashboard.orderStats.byStatus.SHIPPED}</span>
            </div>
            <div className="h-px bg-gray-200"></div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Processing</span>
              <span className="text-lg font-bold text-orange-600">{dashboard.orderStats.byStatus.PROCESSING}</span>
            </div>
            <div className="h-px bg-gray-200"></div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Confirmed</span>
              <span className="text-lg font-bold text-purple-600">{dashboard.orderStats.byStatus.CONFIRMED}</span>
            </div>
            <div className="h-px bg-gray-200"></div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Pending</span>
              <span className="text-lg font-bold text-yellow-600">{dashboard.orderStats.byStatus.PENDING}</span>
            </div>
            <div className="h-px bg-gray-200"></div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Cancelled</span>
              <span className="text-lg font-bold text-red-600">{dashboard.orderStats.byStatus.CANCELLED}</span>
            </div>
          </div>
        </Card>

        {/* Customer & Review Stats */}
        <Card title="Customer & Review Stats" subtitle="Customer and review metrics">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Active Customers</span>
              <span className="text-lg font-bold text-blue-600">{dashboard.customerStats.activeCustomers}</span>
            </div>
            <div className="h-px bg-gray-200"></div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Avg Rating</span>
              <span className="text-lg font-bold text-yellow-600">
                {dashboard.reviewStats.averageRating.toFixed(1)} ★
              </span>
            </div>
            <div className="h-px bg-gray-200"></div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Pending Reviews</span>
              <span className="text-lg font-bold text-orange-600">{dashboard.reviewStats.pendingReviews}</span>
            </div>
            <div className="h-px bg-gray-200"></div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Rejected Reviews</span>
              <span className="text-lg font-bold text-red-600">{dashboard.reviewStats.rejectedReviews}</span>
            </div>
            <div className="h-px bg-gray-200"></div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Total Coupons</span>
              <span className="text-lg font-bold text-pink-600">{dashboard.couponStats.totalCoupons}</span>
            </div>
            <div className="h-px bg-gray-200"></div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Coupons Used</span>
              <span className="text-lg font-bold text-green-600">{dashboard.couponStats.totalUsed}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Section */}
      <OrdersChart stats={dashboard?.orderStats?.byStatus} />

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentOrdersTable orders={dashboard?.recentOrders} />
        <RecentUsersTable users={recentUsers} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LowStockProductsTable products={lowStockProducts} />
        <TopProductsTable products={topProducts} />
      </div>

      {/* Quick Actions */}
      <Card title="Quick Actions" subtitle="Manage your store">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-1">
          {quickActions.map((action) => (
            <button
              key={action.route}
              onClick={() => navigate(action.route)}
              className="group flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-[#F6EEE7] hover:bg-[#F6EEE7] hover:border-[#6D3A0D] transition-all duration-200 cursor-pointer active:scale-95"
            >
              <span className={`${action.color} group-hover:scale-110 transition-transform`}>
                {action.icon}
              </span>
              <span className="font-medium text-[#6D3A0D] group-hover:font-semibold">
                {action.label}
              </span>
              <svg
                className="w-4 h-4 text-[#6D3A0D] opacity-0 group-hover:opacity-100 transition-opacity ml-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          ))}
        </div>
      </Card>
    </div>
  )
}
